import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Line } from 'react-chartjs-2';
import { makeStyles } from '@material-ui/core/styles';
import { withTheme } from '@material-ui/core/styles';

import usePrevious from '../hooks/usePrevious';

const useStyles = makeStyles(() => ({
  container: {
    height: '100%',
    width: '100%',
  },
}));

const Chart = ({ data, dateRange, theme: { palette } }) => {
  const classes = useStyles();
  const [chartData, setChartData] = useState({
    years: [],
    unlimited: [],
    single: [],
    zero: [],
  });
  const prevData = usePrevious(data);

  // TODO: update date range to be global variable
  useEffect(() => {
    if (!dateRange) return;
    if (prevData !== data) {
      return setChartData(data);
    }
    let newData = {
      years: data.years.slice(dateRange[0] - 1972, dateRange[1] - 1972 + 1),
      unlimited: data.unlimited.slice(
        dateRange[0] - 1972,
        dateRange[1] - 1972 + 1
      ),
      single: data.single.slice(dateRange[0] - 1972, dateRange[1] - 1972 + 1),
      zero: data.zero.slice(dateRange[0] - 1972, dateRange[1] - 1972 + 1),
    };
    // console.log('useEffect - new chart data', newData);
    setChartData(newData);
  }, [dateRange, data, prevData]);

  let chartDataset = {
    labels: chartData.years,
    datasets: [
      {
        data: chartData.unlimited,
        label: 'Unlimited',
        borderColor: palette.product.unlimited.light, // '#E3686D',
        backgroundColor: palette.product.unlimited.light, // '#E3686D',
        radius: 0, // 2,
        hitRadius: 6,
        fill: false,
      },
      {
        data: chartData.single,
        label: 'Single Event',
        borderColor: palette.product.single.light, // '#42A5F5',
        backgroundColor: palette.product.single.light, // '#42A5F5',
        radius: 0, // 2,
        hitRadius: 6,
        lineTension: 0.1, // 0.2,
        fill: false,
      },
      {
        data: chartData.zero,
        label: 'Zero Events',
        borderColor: palette.product.zero.light, // '#3ECF8E',
        backgroundColor: palette.product.zero.light, // '#3ECF8E',
        radius: 0, // 2,
        hitRadius: 6,
        lineTension: 0.1, // 0.2,
        fill: false,
      },
    ],
  };

  let chartOptions = {
    title: {
      display: true,
      text: 'iDemand Product Comparison',
      fontSize: 14,
      fontColor: palette.text.primary, // '#000',
      padding: 15,
    },
    legend: {
      display: true,
      position: 'bottom',
      labels: {
        fontColor: palette.text.primary,
        boxWidth: 20,
        padding: 15,
      },
    },
    tooltips: {
      mode: 'index',
      titleMarginBottom: 8,
      bodySpacing: 6,
      xPadding: 8,
      yPadding: 8,
      position: 'nearest',
      callbacks: {
        label: function (tooltipItem, data) {
          return (
            data.datasets[tooltipItem.datasetIndex].label +
            ': ' +
            tooltipItem.yLabel +
            '%'
          );
        },
      },
    },
    scales: {
      xAxes: [
        {
          display: true,
          color: palette.text.secondary,
          gridLines: {
            display: false,
            color: palette.text.secondary,
          },
          ticks: {
            maxTicksLimit: 11,
            fontColor: palette.text.secondary,
          },
        },
      ],
      yAxes: [
        {
          type: 'linear',
          color: palette.text.secondary,
          gridLines: {
            color: palette.divider,
          },
          ticks: {
            min: 50, // 40,
            max: 200,
            fontColor: palette.text.secondary,
            callback: function (value) {
              return value + '%';
            },
            stepSize: 25,
          },
          scaleLabel: {
            display: true,
            fontColor: palette.text.secondary,
            labelString: '% of Unlimited Premium',
          },
        },
      ],
    },
    animation: {
      duration: 4000,
      numSteps: 60,
      easing: 'easeInOutElastic',
    },
    maintainAspectRatio: false,
  };

  return (
    <div className={classes.container}>
      <Line
        data={chartDataset}
        width={null}
        height={null}
        options={chartOptions}
      />
    </div>
  );
};

Chart.propTypes = {
  data: PropTypes.shape({
    years: PropTypes.array.isRequired,
    unlimited: PropTypes.array.isRequired,
    single: PropTypes.array.isRequired,
    zero: PropTypes.array.isRequired,
  }),
  dateRange: PropTypes.array.isRequired,
};

export default withTheme(Chart);
